import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosClientSignIn } from '../axios/api.js';

// ==================== ASYNC THUNKS ====================

// Fetch HR Dashboard
export const fetchHRDashboard = createAsyncThunk(
  'hr/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get('/hr/dashboard');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Job Posting Thunks
export const fetchJobPostings = createAsyncThunk(
  'hr/fetchJobPostings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClientSignIn.get(`/hr/jobs?${queryString}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchJobPostingById = createAsyncThunk(
  'hr/fetchJobPostingById',
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/hr/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createJobPosting = createAsyncThunk(
  'hr/createJobPosting',
  async (jobData, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post('/hr/jobs', jobData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateJobPosting = createAsyncThunk(
  'hr/updateJobPosting',
  async ({ jobId, updates }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.put(`/hr/jobs/${jobId}`, updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteJobPosting = createAsyncThunk(
  'hr/deleteJobPosting',
  async (jobId, { rejectWithValue }) => {
    try {
      await axiosClientSignIn.delete(`/hr/jobs/${jobId}`);
      return jobId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Job Application Thunks
export const fetchApplications = createAsyncThunk(
  'hr/fetchApplications',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClientSignIn.get(`/hr/applications?${queryString}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== EXTERNAL PLATFORM THUNKS ====================
export const postJobToLinkedIn = createAsyncThunk(
  'hr/postJobToLinkedIn',
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post(`/hr/jobs/${jobId}/post-linkedin`);
      return { jobId, ...response.data };
    } catch (error) {
      return rejectWithValue({ jobId, ...(error.response?.data || { message: error.message }) });
    }
  }
);

export const postJobToWorkIndia = createAsyncThunk(
  'hr/postJobToWorkIndia',
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post(`/hr/jobs/${jobId}/post-workindia`);
      return { jobId, ...response.data };
    } catch (error) {
      return rejectWithValue({ jobId, ...(error.response?.data || { message: error.message }) });
    }
  }
);

export const getExternalPlatformStatus = createAsyncThunk(
  'hr/getExternalPlatformStatus',
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/hr/jobs/${jobId}/external-status`);
      return { jobId, ...response.data };
    } catch (error) {
      return rejectWithValue({ jobId, ...(error.response?.data || { message: error.message }) });
    }
  }
);

export const syncExternalApplications = createAsyncThunk(
  'hr/syncExternalApplications',
  async ({ jobId, platform }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post(`/hr/jobs/${jobId}/sync-applications`, { platform });
      return { jobId, platform, ...response.data };
    } catch (error) {
      return rejectWithValue({ jobId, platform, ...(error.response?.data || { message: error.message }) });
    }
  }
);

export const closeExternalJob = createAsyncThunk(
  'hr/closeExternalJob',
  async ({ jobId, platform }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post(`/hr/jobs/${jobId}/close-external`, { platform });
      return { jobId, platform, ...response.data };
    } catch (error) {
      return rejectWithValue({ jobId, platform, ...(error.response?.data || { message: error.message }) });
    }
  }
);

export const fetchJobApplications = createAsyncThunk(
  'hr/fetchJobApplications',
  async ({ jobId, params = {} }, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClientSignIn.get(`/hr/jobs/${jobId}/applications?${queryString}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchApplicationById = createAsyncThunk(
  'hr/fetchApplicationById',
  async (applicationId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/hr/applications/${applicationId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateApplicationStatus = createAsyncThunk(
  'hr/updateApplicationStatus',
  async ({ applicationId, status, rejectionReason, hrNotes, rating }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.put(`/hr/applications/${applicationId}/status`, {
        status,
        rejectionReason,
        hrNotes,
        rating
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Employee Thunks
export const fetchEmployees = createAsyncThunk(
  'hr/fetchEmployees',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClientSignIn.get(`/hr/employees?${queryString}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== HR SLICE ====================

const hrSlice = createSlice({
  name: 'hr',
  initialState: {
    // Dashboard
    dashboard: {
      jobs: { total: 0, active: 0, draft: 0, closed: 0 },
      applications: { total: 0, new: 0, shortlisted: 0, selected: 0 },
      employees: { total: 0, active: 0, inactive: 0 },
      recentApplications: [],
      jobsByDepartment: []
    },
    dashboardLoading: false,

    // Job Postings
    jobPostings: [],
    selectedJobPosting: null,
    jobPostingsLoading: false,
    jobPostingsPagination: {
      currentPage: 1,
      totalPages: 1,
      totalJobs: 0,
      hasMore: false
    },

    // Applications
    applications: [],
    selectedApplication: null,
    applicationsLoading: false,
    applicationsPagination: {
      currentPage: 1,
      totalPages: 1,
      totalApplications: 0,
      hasMore: false
    },

    // Employees
    employees: [],
    selectedEmployee: null,
    employeesLoading: false,
    employeesPagination: {
      currentPage: 1,
      totalPages: 1,
      totalEmployees: 0,
      hasMore: false
    },

    // External job platform state (by jobId)
    externalStatusByJob: {}, // { [jobId]: Array<status> }
    externalOpsLoading: {},  // { [jobId]: boolean }

    // UI State
    currentTab: 'jobs',
    filters: {
      jobStatus: '',
      applicationStatus: '',
      department: '',
      search: ''
    },

    // General
    loading: false,
    error: null,
    successMessage: null
  },

  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
    setSelectedJobPosting: (state, action) => {
      state.selectedJobPosting = action.payload;
    },
    setSelectedApplication: (state, action) => {
      state.selectedApplication = action.payload;
    },
    setSelectedEmployee: (state, action) => {
      state.selectedEmployee = action.payload;
    },
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
        jobStatus: '',
        applicationStatus: '',
        department: '',
        search: ''
      };
    }
  },

  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchHRDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.error = null;
      })
      .addCase(fetchHRDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboard = action.payload.dashboard;
      })
      .addCase(fetchHRDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.error = action.payload?.message || 'Failed to fetch dashboard';
      })

      // Job Postings
      .addCase(fetchJobPostings.pending, (state) => {
        state.jobPostingsLoading = true;
        state.error = null;
      })
      .addCase(fetchJobPostings.fulfilled, (state, action) => {
        state.jobPostingsLoading = false;
        state.jobPostings = action.payload.jobPostings;
        state.jobPostingsPagination = action.payload.pagination;
      })
      .addCase(fetchJobPostings.rejected, (state, action) => {
        state.jobPostingsLoading = false;
        state.error = action.payload?.message || 'Failed to fetch job postings';
      })

      // Single Job Posting
      .addCase(fetchJobPostingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobPostingById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedJobPosting = action.payload.jobPosting;
      })
      .addCase(fetchJobPostingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch job posting';
      })

      // Create Job Posting
      .addCase(createJobPosting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJobPosting.fulfilled, (state, action) => {
        state.loading = false;
        state.jobPostings.unshift(action.payload.jobPosting);
        state.successMessage = 'Job posting created successfully';
      })
      .addCase(createJobPosting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to create job posting';
      })

      // Update Job Posting
      .addCase(updateJobPosting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJobPosting.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.jobPostings.findIndex(job => job._id === action.payload.jobPosting._id);
        if (index !== -1) {
          state.jobPostings[index] = action.payload.jobPosting;
        }
        state.selectedJobPosting = action.payload.jobPosting;
        state.successMessage = 'Job posting updated successfully';
      })
      .addCase(updateJobPosting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update job posting';
      })

      // Delete Job Posting
      .addCase(deleteJobPosting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJobPosting.fulfilled, (state, action) => {
        state.loading = false;
        state.jobPostings = state.jobPostings.filter(job => job._id !== action.payload);
        if (state.selectedJobPosting?._id === action.payload) {
          state.selectedJobPosting = null;
        }
        state.successMessage = 'Job posting deleted successfully';
      })
      .addCase(deleteJobPosting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to delete job posting';
      })

      // Applications
      .addCase(fetchApplications.pending, (state) => {
        state.applicationsLoading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.applicationsLoading = false;
        state.applications = action.payload.applications;
        state.applicationsPagination = action.payload.pagination;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.applicationsLoading = false;
        state.error = action.payload?.message || 'Failed to fetch applications';
      })

      // Job Applications
      .addCase(fetchJobApplications.pending, (state) => {
        state.applicationsLoading = true;
        state.error = null;
      })
      .addCase(fetchJobApplications.fulfilled, (state, action) => {
        state.applicationsLoading = false;
        state.applications = action.payload.applications;
        state.applicationsPagination = action.payload.pagination;
      })
      .addCase(fetchJobApplications.rejected, (state, action) => {
        state.applicationsLoading = false;
        state.error = action.payload?.message || 'Failed to fetch job applications';
      })

      // Single Application
      .addCase(fetchApplicationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplicationById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedApplication = action.payload.application;
      })
      .addCase(fetchApplicationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch application';
      })

      // Update Application Status
      .addCase(updateApplicationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateApplicationStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.applications.findIndex(app => app._id === action.payload.application._id);
        if (index !== -1) {
          state.applications[index] = action.payload.application;
        }
        state.selectedApplication = action.payload.application;
        state.successMessage = 'Application status updated successfully';
      })
      .addCase(updateApplicationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update application status';
      })

      // Employees
      .addCase(fetchEmployees.pending, (state) => {
        state.employeesLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.employeesLoading = false;
        state.employees = action.payload.employees;
        state.employeesPagination = action.payload.pagination;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.employeesLoading = false;
        state.error = action.payload?.message || 'Failed to fetch employees';
      })

      // ==================== EXTERNAL PLATFORM REDUCERS ====================
      // Post to LinkedIn
      .addCase(postJobToLinkedIn.pending, (state, action) => {
        const jobId = action.meta.arg;
        state.externalOpsLoading[jobId] = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(postJobToLinkedIn.fulfilled, (state, action) => {
        const { jobId, message } = action.payload;
        state.externalOpsLoading[jobId] = false;
        state.successMessage = message || 'Job posted to LinkedIn successfully';
      })
      .addCase(postJobToLinkedIn.rejected, (state, action) => {
        const jobId = action.payload?.jobId || action.meta.arg;
        state.externalOpsLoading[jobId] = false;
        state.error = action.payload?.message || 'Failed to post job to LinkedIn';
      })

      // Post to WorkIndia
      .addCase(postJobToWorkIndia.pending, (state, action) => {
        const jobId = action.meta.arg;
        state.externalOpsLoading[jobId] = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(postJobToWorkIndia.fulfilled, (state, action) => {
        const { jobId, message } = action.payload;
        state.externalOpsLoading[jobId] = false;
        state.successMessage = message || 'Job posted to WorkIndia successfully';
      })
      .addCase(postJobToWorkIndia.rejected, (state, action) => {
        const jobId = action.payload?.jobId || action.meta.arg;
        state.externalOpsLoading[jobId] = false;
        state.error = action.payload?.message || 'Failed to post job to WorkIndia';
      })

      // Get external status
      .addCase(getExternalPlatformStatus.pending, (state, action) => {
        const jobId = action.meta.arg;
        state.externalOpsLoading[jobId] = true;
        state.error = null;
      })
      .addCase(getExternalPlatformStatus.fulfilled, (state, action) => {
        const { jobId, externalPostings } = action.payload;
        state.externalOpsLoading[jobId] = false;
        state.externalStatusByJob[jobId] = externalPostings || [];
      })
      .addCase(getExternalPlatformStatus.rejected, (state, action) => {
        const jobId = action.payload?.jobId || action.meta.arg;
        state.externalOpsLoading[jobId] = false;
        state.error = action.payload?.message || 'Failed to fetch external platform status';
      })

      // Sync applications from external platform
      .addCase(syncExternalApplications.pending, (state, action) => {
        const { jobId } = action.meta.arg;
        state.externalOpsLoading[jobId] = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(syncExternalApplications.fulfilled, (state, action) => {
        const { jobId, platform, message, newApplications, totalApplications } = action.payload;
        state.externalOpsLoading[jobId] = false;
        state.successMessage = message || `Synced applications from ${platform}. New: ${newApplications || 0}`;
      })
      .addCase(syncExternalApplications.rejected, (state, action) => {
        const jobId = action.payload?.jobId || action.meta.arg?.jobId;
        if (jobId) state.externalOpsLoading[jobId] = false;
        state.error = action.payload?.message || 'Failed to sync external applications';
      })

      // Close external job
      .addCase(closeExternalJob.pending, (state, action) => {
        const { jobId } = action.meta.arg;
        state.externalOpsLoading[jobId] = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(closeExternalJob.fulfilled, (state, action) => {
        const { jobId, platform, message } = action.payload;
        state.externalOpsLoading[jobId] = false;
        state.successMessage = message || `Closed job on ${platform} successfully`;
      })
      .addCase(closeExternalJob.rejected, (state, action) => {
        const jobId = action.payload?.jobId || action.meta.arg?.jobId;
        if (jobId) state.externalOpsLoading[jobId] = false;
        state.error = action.payload?.message || 'Failed to close external job';
      });
  }
});

export const {
  clearError,
  clearSuccessMessage,
  setCurrentTab,
  setSelectedJobPosting,
  setSelectedApplication,
  setSelectedEmployee,
  updateFilters,
  resetFilters
} = hrSlice.actions;

export default hrSlice.reducer;
